import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Lucide from "lucide-react";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { SectionLabel, GhostNumber, HeroHeading, CTA, EditorialMarquee } from "@/components/site/Bits";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/Reveal";
import { Counter } from "@/components/site/Counter";
import {
  IMAGES, HOME_STATS, TRUST_SIGNALS, SECTORS, HOME_STEPS,
  COMPLIANCE_TEASER, UNIVERSITIES, HOME_BENEFITS, CONTACT,
} from "@/data/content";

function Icon({ name, className }) {
  const C = Lucide[name] || Lucide.Circle;
  return <C className={className} />;
}

/* ---------------- HERO ---------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yBadge = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white pt-32 lg:pt-40 pb-16 lg:pb-24" data-testid="home-hero">
      <div className="absolute top-0 right-0 w-[45%] h-full flag-stripes opacity-70 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#C8102E]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel testId="hero-overline">
              Southampton and London's specialist international candidate employment partner
            </SectionLabel>
          </Reveal>

          <HeroHeading
            testId="hero-heading"
            className="mt-6 font-outfit font-extrabold text-[#0A1F44] text-4xl sm:text-6xl lg:text-[4.6rem] leading-[0.95] tracking-tight"
            lines={[
              <>Opening doors to</>,
              <span key="r" className="text-[#C8102E]">exceptional</span>,
              <>international candidates</>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-7 text-[#12294D]/80 text-lg leading-relaxed max-w-2xl font-manrope"
          >
            We connect our abundant international talent pool with job consultancies across
            Southampton and London — placing candidates in part-time, seasonal, full-time,
            fixed-term, and permanent roles. Compliantly, efficiently, and at scale.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-4 text-[#12294D]/60 text-base leading-relaxed max-w-2xl font-manrope"
          >
            Our international candidates are open to all employment types — from flexible part-time
            and seasonal roles to full-time, fixed-term, and permanent positions. Whatever your
            consultancy clients need, our talent pool is ready.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-9"
          >
            <CTA to="/for-consultancies" testId="hero-cta-button">
              For consultancies <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </CTA>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
            data-testid="hero-trust-signals"
          >
            {TRUST_SIGNALS.map((t) => (
              <span key={t} className="flex items-center gap-2 text-sm font-semibold text-[#0A1F44]/75 font-manrope">
                <Check className="h-4 w-4 text-[#C8102E]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -left-4 -top-4 w-24 h-24 border-t-4 border-l-4 border-[#C8102E] rounded-tl-2xl" />
            <div className="relative rounded-[1.75rem] overflow-hidden shadow-[0_30px_80px_rgba(10,31,68,0.25)] aspect-[4/5]">
              <motion.img
                style={{ y }}
                src={IMAGES.hospitality}
                alt="International student working in hospitality"
                className="w-full h-[118%] object-cover -mt-[9%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/40 via-transparent to-transparent" />
            </div>
            <motion.div
              style={{ y: yBadge }}
              className="absolute -bottom-6 -left-6 bg-[#0A1F44] text-white rounded-2xl px-6 py-5 shadow-xl grain overflow-hidden"
            >
              <div className="text-3xl font-outfit font-extrabold text-white">
                <Counter to={33000} suffix="+" />
              </div>
              <div className="text-xs text-white/70 font-manrope mt-1 max-w-[10rem]">
                Pre-verified international candidates
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- STATS ---------------- */
function StatsBar() {
  return (
    <section className="bg-[#0A1F44] relative overflow-hidden grain" data-testid="home-stats">
      <div className="absolute inset-0 flag-stripes opacity-30" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10 relative">
        {HOME_STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="relative">
              <div className="text-4xl lg:text-5xl font-outfit font-extrabold text-white">
                {s.value !== undefined ? (
                  <>
                    <Counter to={s.value} suffix={s.suffix} />
                    {s.text ? <span className="block text-2xl lg:text-3xl mt-1">{s.text}</span> : null}
                  </>
                ) : (
                  <span className="text-[#C8102E]">{s.text}</span>
                )}
              </div>
              <div className="mt-3 h-[3px] w-10 bg-[#C8102E]" />
              <p className="mt-4 text-white/70 text-sm leading-relaxed font-manrope">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- WHO WE SERVE ---------------- */
function WhoWeServe() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="home-who-we-serve">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal><SectionLabel>Who we serve</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                Built exclusively for job consultancies. Delivering across two cities.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#12294D]/75 leading-relaxed font-manrope">
                UK Student Job operates exclusively B2B — connecting our international candidate
                talent pool with job consultancies across Southampton and London, through a
                compliance infrastructure that no generalist agency can match. We serve
                consultancies. We do not work directly with business organisations or employers,
                and we never will. Our sole focus is on empowering our consultancy partners to
                place exceptional international candidates with confidence.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <div className="relative bg-[#0A1F44] rounded-[1.75rem] p-9 lg:p-12 text-white overflow-hidden grain">
                <div className="absolute inset-0 flag-stripes opacity-30 pointer-events-none" />
                <div className="relative">
                  <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#C8102E]">For Job Consultancies</span>
                  <p className="mt-4 text-white/85 leading-relaxed font-manrope">
                    Expand your placement capacity with an abundant, pre-verified international talent
                    pool across Southampton and London — covering part-time, seasonal, full-time,
                    fixed-term, and permanent roles — with the entire compliance burden significantly
                    reduced for your business.
                  </p>
                  <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                    {HOME_BENEFITS.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-white/80 font-manrope">
                        <Check className="h-4 w-4 text-[#C8102E] mt-0.5 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/for-consultancies"
                    data-testid="who-explore-cta"
                    className="mt-9 inline-flex items-center gap-2 text-white font-semibold border-b-2 border-[#C8102E] pb-1 hover:gap-3 transition-all duration-300"
                  >
                    Explore our consultancy partnership <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */
function HowItWorks() {
  return (
    <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="home-how-it-works">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal><SectionLabel>The process</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight max-w-2xl">
            Simple, professional, managed for you
          </h2>
        </Reveal>
        <RevealStagger className="mt-16 grid md:grid-cols-3 gap-8">
          {HOME_STEPS.map((s, i) => (
            <RevealItem key={i}>
              <div className="relative bg-white rounded-2xl p-9 h-full border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)] hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(10,31,68,0.12)] transition-transform transition-shadow duration-500">
                <GhostNumber className="absolute top-4 right-6 text-7xl">{`0${i + 1}`}</GhostNumber>
                <div className="text-[#C8102E] font-outfit font-extrabold text-lg">{`0${i + 1}`}</div>
                <h3 className="mt-3 font-outfit font-bold text-xl text-[#0A1F44]">{s.title}</h3>
                <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{s.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}

/* ---------------- SECTORS ---------------- */
function Sectors() {
  const spans = ["lg:col-span-2", "lg:col-span-1", "lg:col-span-1", "lg:col-span-1", "lg:col-span-1", "lg:col-span-2"];
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="home-sectors">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><SectionLabel>Sectors we serve</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
              Where our international candidates excel
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[#12294D]/75 leading-relaxed font-manrope">
              Our international candidates are pre-screened and matched against your consultancy
              clients' specific sector requirements across Southampton and London — for all role
              types, from casual and seasonal to permanent positions.
            </p>
          </Reveal>
        </div>

        <RevealStagger className="mt-14 grid lg:grid-cols-3 gap-6" stagger={0.08}>
          {SECTORS.map((s, i) => (
            <RevealItem key={s.title} className={spans[i]}>
              <div className="group relative h-full bg-[#F4F6F9] rounded-2xl p-8 border border-[#0A1F44]/8 overflow-hidden hover:-translate-y-2 transition-transform duration-500">
                <div className="absolute right-0 top-0 h-1 w-0 bg-[#C8102E] group-hover:w-full transition-all duration-500" />
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-[#0A1F44] text-white group-hover:bg-[#C8102E] transition-colors duration-300">
                  <Icon name={s.icon} className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-outfit font-bold text-xl text-[#0A1F44]">{s.title}</h3>
                <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{s.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}

/* ---------------- COMPLIANCE TEASER ---------------- */
function ComplianceTeaser() {
  return (
    <section className="relative bg-[#0A1F44] py-24 lg:py-32 overflow-hidden grain" data-testid="home-compliance">
      <div className="absolute inset-0 flag-stripes opacity-25" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><SectionLabel light>Our compliance assurance</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-white text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                Extremely low compliance risk. Professionally managed.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-white/70 leading-relaxed font-manrope">
                International candidate employment carries genuine compliance complexity. UK Student
                Job manages every layer of the process to keep risk extremely low — for every
                consultancy partner, every placement, every time.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <CTA to="/compliance" variant="secondary-light" testId="home-compliance-cta">
                View our full compliance process <ArrowRight className="h-4 w-4" />
              </CTA>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <RevealStagger className="grid sm:grid-cols-2 gap-5">
              {COMPLIANCE_TEASER.map((c) => (
                <RevealItem key={c.n}>
                  <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
                    <div className="text-[#C8102E] font-outfit font-extrabold text-2xl">{c.n}</div>
                    <h3 className="mt-3 font-outfit font-bold text-lg text-white">{c.title}</h3>
                    <p className="mt-2 text-white/65 text-sm leading-relaxed font-manrope">{c.body}</p>
                  </div>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- UNIVERSITIES ---------------- */
function Universities() {
  return (
    <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="home-universities">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><SectionLabel>Our source universities</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
              An abundant international talent pool from Southampton's two leading universities
            </h2>
          </Reveal>
        </div>
        <RevealStagger className="mt-14 grid md:grid-cols-2 gap-6">
          {UNIVERSITIES.slice(0, 2).map((u, i) => (
            <RevealItem key={u.name}>
              <div className="relative h-full bg-white rounded-2xl p-9 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)]">
                <div className="flex items-start justify-between">
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-[#0A1F44] text-white">
                    <Lucide.Building2 className="h-7 w-7" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#C8102E]">{u.tag}</span>
                </div>
                <h3 className="mt-6 font-outfit font-bold text-2xl text-[#0A1F44]">{u.name}</h3>
                <ul className="mt-5 space-y-3">
                  {(i === 0 ? u.points.slice(0, 2) : u.points).map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-[#12294D]/75 font-manrope">
                      <Check className="h-4 w-4 text-[#C8102E] mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <Reveal delay={0.1}>
          <p className="mt-8 text-[#12294D]/60 font-manrope max-w-3xl">
            International candidates from both universities are placed with consultancy partners
            across Southampton and London — in all role types from part-time to permanent.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCTA() {
  const details = [
    { icon: "Mail", label: "Email us", value: CONTACT.emailBusiness, href: `mailto:${CONTACT.emailBusiness}` },
    { icon: "Mail", label: "Email us", value: CONTACT.emailInfo, href: `mailto:${CONTACT.emailInfo}` },
    { icon: "Globe", label: "Visit us", value: CONTACT.website, href: `https://${CONTACT.website}` },
    { icon: "MapPin", label: "Serving", value: CONTACT.coverage },
  ];
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="home-final-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="relative rounded-[2rem] bg-[#0A1F44] overflow-hidden grain p-10 lg:p-16">
          <div className="absolute inset-0 flag-stripes opacity-25" />
          <div className="absolute -right-16 -bottom-16 w-80 h-80 rounded-full bg-[#C8102E]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <h2 className="font-outfit font-extrabold text-white text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                  Partner with UK Student Job today
                </h2>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-6 text-white/75 leading-relaxed font-manrope max-w-xl">
                  If you are a job consultancy looking for a reliable, abundant international
                  candidate pipeline across Southampton or London — for part-time, seasonal,
                  full-time, fixed-term, or permanent roles — we are ready to build that partnership.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <CTA to="/for-consultancies" testId="final-cta-button">
                  For consultancies <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </CTA>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <RevealStagger className="grid sm:grid-cols-2 gap-4">
                {details.map((d, i) => (
                  <RevealItem key={i}>
                    {d.href ? (
                      <a href={d.href} className="block bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors duration-300">
                        <div className="flex items-center gap-2 text-[#C8102E]">
                          <Icon name={d.icon} className="h-4 w-4" />
                          <span className="text-xs uppercase tracking-wider font-semibold">{d.label}</span>
                        </div>
                        <div className="mt-2 text-white font-manrope font-semibold text-sm break-all">{d.value}</div>
                      </a>
                    ) : (
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-2 text-[#C8102E]">
                          <Icon name={d.icon} className="h-4 w-4" />
                          <span className="text-xs uppercase tracking-wider font-semibold">{d.label}</span>
                        </div>
                        <div className="mt-2 text-white font-manrope font-semibold text-sm">{d.value}</div>
                      </div>
                    )}
                  </RevealItem>
                ))}
              </RevealStagger>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo(
    "UK Student Job — Opening doors to exceptional international candidates | Southampton · London",
    "UK Student Job connects an abundant pool of pre-verified international candidates from Southampton's two universities with job consultancies across Southampton and London. All role types. Extremely low compliance risk."
  );
  return (
    <>
      <Hero />
      <StatsBar />
      <WhoWeServe />
      <HowItWorks />
      <Sectors />
      <ComplianceTeaser />
      <EditorialMarquee />
      <Universities />
      <FinalCTA />
    </>
  );
}
